import { Message } from "@arco-design/web-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { PAYMENT_RECORD_PAGE_SIZE, PAYMENT_RECORD_POLL_INTERVAL_MS } from "../config";
import {
  applyCardPaymentEReceipt,
  createCardPaymentAccount,
  createCardPaymentTransfer,
  createPaymentRefund,
  downloadCardPaymentEReceipt,
  fetchCardPaymentOverview,
  fetchPaymentDetail,
  queryCardPaymentEReceipt,
  refreshBankCardCollection,
  refreshQrCollection,
  resolveBankCardCollection,
  resolveQrCollection,
} from "../repo";
import { paymentValidationErrorMessage, refundableAmount } from "../service";
import type {
  BankCardCollectionResolvePayload,
  CardPaymentEReceiptItem,
  CardPaymentOverviewPayload,
  CardPaymentRecordItem,
  PaymentRecord,
  PaymentRecordDetail,
  QrCollectionResolvePayload,
} from "../types";

/** 管理二维码收款通道的查询、详情和退款状态。 */
export function usePaymentValidationRuntime() {
  const [teamId, setTeamId] = useState("");
  const [activeTeamId, setActiveTeamId] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<QrCollectionResolvePayload>();
  const [errorMessage, setErrorMessage] = useState("");
  const [detailVisible, setDetailVisible] = useState(false);
  const [detail, setDetail] = useState<PaymentRecordDetail>();
  const [refundPayment, setRefundPayment] = useState<PaymentRecord>();
  const [refundAmount, setRefundAmount] = useState<number>();
  const [refundErrorMessage, setRefundErrorMessage] = useState("");

  const resolveMutation = useMutation({
    mutationFn: ({ selectedTeamId, selectedPage }: { selectedTeamId: string; selectedPage: number }) =>
      resolveQrCollection(selectedTeamId, selectedPage, PAYMENT_RECORD_PAGE_SIZE),
  });
  const detailMutation = useMutation({ mutationFn: fetchPaymentDetail });
  const refundMutation = useMutation({
    mutationFn: ({ paymentId, amount }: { paymentId: string; amount: number }) =>
      createPaymentRefund(paymentId, amount),
  });
  const qrRefreshQuery = useQuery({
    queryKey: ["payment-validation", "qr-collection-refresh", activeTeamId, page],
    queryFn: () => refreshQrCollection(activeTeamId, page, PAYMENT_RECORD_PAGE_SIZE),
    enabled: Boolean(activeTeamId) && Boolean(result),
    refetchInterval: PAYMENT_RECORD_POLL_INTERVAL_MS,
    retry: false,
  });

  useEffect(() => {
    if (qrRefreshQuery.data) {
      setResult(qrRefreshQuery.data);
    }
  }, [qrRefreshQuery.data]);

  /** 查询指定团编号与分页，并统一更新结果状态。 */
  async function loadCollection(selectedTeamId: string, selectedPage: number) {
    setErrorMessage("");
    try {
      const nextResult = await resolveMutation.mutateAsync({ selectedTeamId, selectedPage });
      setResult(nextResult);
      setPage(selectedPage);
    } catch (error) {
      setResult(undefined);
      setErrorMessage(paymentValidationErrorMessage(error, "二维码收款查询失败，请稍后重试。"));
    }
  }

  return {
    teamId,
    setTeamId,
    activeTeamId,
    page,
    pageSize: PAYMENT_RECORD_PAGE_SIZE,
    result,
    errorMessage,
    loading: resolveMutation.isPending,
    detailVisible,
    detail,
    detailLoading: detailMutation.isPending,
    refundPayment,
    refundAmount,
    setRefundAmount,
    refundErrorMessage,
    refundLoading: refundMutation.isPending,
    async handleSearch() {
      const normalized = teamId.trim();
      if (!normalized) {
        setErrorMessage("请输入团编号");
        return;
      }
      setResult(undefined);
      setActiveTeamId(normalized);
      await loadCollection(normalized, 1);
    },
    async handlePageChange(nextPage: number) {
      if (activeTeamId) {
        await loadCollection(activeTeamId, nextPage);
      }
    },
    async openDetail(payment: PaymentRecord) {
      setDetail(undefined);
      setDetailVisible(true);
      try {
        setDetail(await detailMutation.mutateAsync(payment.id));
      } catch (error) {
        setErrorMessage(paymentValidationErrorMessage(error, "付款详情查询失败，请稍后重试。"));
      }
    },
    closeDetail() {
      setDetailVisible(false);
      setDetail(undefined);
    },
    openRefund(payment: PaymentRecord) {
      setRefundErrorMessage("");
      setRefundPayment(payment);
      setRefundAmount(refundableAmount(payment.amount, payment.refunded_amount));
    },
    closeRefund() {
      setRefundPayment(undefined);
      setRefundAmount(undefined);
      setRefundErrorMessage("");
    },
    async submitRefund() {
      if (!refundPayment || refundAmount == null) {
        return;
      }
      setRefundErrorMessage("");
      try {
        await refundMutation.mutateAsync({
          paymentId: refundPayment.id,
          amount: refundAmount,
        });
        setRefundPayment(undefined);
        setRefundAmount(undefined);
        if (activeTeamId) {
          await loadCollection(activeTeamId, page);
        }
      } catch (error) {
        setRefundErrorMessage(paymentValidationErrorMessage(error, "退款提交失败，请稍后重试。"));
      }
    },
  };
}

/** 管理银行卡号收款的团号查询、开卡进度轮询、详情和退款。 */
export function useBankCardCollectionRuntime() {
  const [teamId, setTeamId] = useState("");
  const [activeTeamId, setActiveTeamId] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<BankCardCollectionResolvePayload>();
  const [errorMessage, setErrorMessage] = useState("");
  const [detailVisible, setDetailVisible] = useState(false);
  const [detail, setDetail] = useState<PaymentRecordDetail>();
  const [refundPayment, setRefundPayment] = useState<PaymentRecord>();
  const [refundAmount, setRefundAmount] = useState<number>();
  const [refundErrorMessage, setRefundErrorMessage] = useState("");

  const resolveMutation = useMutation({
    mutationFn: ({ selectedTeamId, selectedPage }: { selectedTeamId: string; selectedPage: number }) =>
      resolveBankCardCollection(selectedTeamId, selectedPage, PAYMENT_RECORD_PAGE_SIZE),
  });
  const detailMutation = useMutation({ mutationFn: fetchPaymentDetail });
  const refundMutation = useMutation({
    mutationFn: ({ paymentId, amount }: { paymentId: string; amount: number }) =>
      createPaymentRefund(paymentId, amount),
  });
  const bankRefreshQuery = useQuery({
    queryKey: ["payment-validation", "bank-card-collection-refresh", activeTeamId, page],
    queryFn: () => refreshBankCardCollection(activeTeamId, page, PAYMENT_RECORD_PAGE_SIZE),
    enabled: Boolean(activeTeamId) && Boolean(result),
    refetchInterval: PAYMENT_RECORD_POLL_INTERVAL_MS,
    retry: false,
  });

  useEffect(() => {
    if (bankRefreshQuery.data) {
      setResult(bankRefreshQuery.data);
    }
  }, [bankRefreshQuery.data]);

  /** 查询指定团编号与分页，并统一更新结果状态。 */
  async function loadCollection(selectedTeamId: string, selectedPage: number) {
    setErrorMessage("");
    try {
      const nextResult = await resolveMutation.mutateAsync({ selectedTeamId, selectedPage });
      setResult(nextResult);
      setPage(selectedPage);
    } catch (error) {
      setResult(undefined);
      setErrorMessage(paymentValidationErrorMessage(error, "银行卡号收款查询失败，请稍后重试。"));
    }
  }

  return {
    teamId,
    setTeamId,
    activeTeamId,
    page,
    pageSize: PAYMENT_RECORD_PAGE_SIZE,
    result,
    errorMessage,
    loading: resolveMutation.isPending,
    detailVisible,
    detail,
    detailLoading: detailMutation.isPending,
    refundPayment,
    refundAmount,
    setRefundAmount,
    refundErrorMessage,
    refundLoading: refundMutation.isPending,
    async handleSearch() {
      const normalized = teamId.trim();
      if (!normalized) {
        setErrorMessage("请输入团编号");
        return;
      }
      setResult(undefined);
      setActiveTeamId(normalized);
      await loadCollection(normalized, 1);
    },
    async handlePageChange(nextPage: number) {
      if (activeTeamId) {
        await loadCollection(activeTeamId, nextPage);
      }
    },
    async openDetail(payment: PaymentRecord) {
      setDetail(undefined);
      setDetailVisible(true);
      try {
        setDetail(await detailMutation.mutateAsync(payment.id));
      } catch (error) {
        setErrorMessage(paymentValidationErrorMessage(error, "付款详情查询失败，请稍后重试。"));
      }
    },
    closeDetail() {
      setDetailVisible(false);
      setDetail(undefined);
    },
    openRefund(payment: PaymentRecord) {
      setRefundErrorMessage("");
      setRefundPayment(payment);
      setRefundAmount(refundableAmount(payment.amount, payment.refunded_amount));
    },
    closeRefund() {
      setRefundPayment(undefined);
      setRefundAmount(undefined);
      setRefundErrorMessage("");
    },
    async submitRefund() {
      if (!refundPayment || refundAmount == null) {
        return;
      }
      setRefundErrorMessage("");
      try {
        await refundMutation.mutateAsync({
          paymentId: refundPayment.id,
          amount: refundAmount,
        });
        setRefundPayment(undefined);
        setRefundAmount(undefined);
        if (activeTeamId) {
          await loadCollection(activeTeamId, page);
        }
      } catch (error) {
        setRefundErrorMessage(paymentValidationErrorMessage(error, "退款提交失败，请稍后重试。"));
      }
    },
  };
}

/** 管理卡支付可切换付款账户总览与转账。 */
export function useCardPaymentRuntime() {
  const [page, setPage] = useState(1);
  const [selectedAccountNo, setSelectedAccountNo] = useState("");
  const [result, setResult] = useState<CardPaymentOverviewPayload>();
  const [errorMessage, setErrorMessage] = useState("");
  const [transferVisible, setTransferVisible] = useState(false);
  const [transferAmount, setTransferAmount] = useState<number | undefined>();
  const [transferReceiveAccountNo, setTransferReceiveAccountNo] = useState("");
  const [transferReceiveAccountName, setTransferReceiveAccountName] = useState("");
  const [transferReceiveOpenBankName, setTransferReceiveOpenBankName] = useState("");
  const [transferPayAccountType, setTransferPayAccountType] = useState<"00" | "01">("01");
  const [transferErrorMessage, setTransferErrorMessage] = useState("");
  const [addAccountVisible, setAddAccountVisible] = useState(false);
  const [addAccountNo, setAddAccountNo] = useState("");
  const [addAccountName, setAddAccountName] = useState("");
  const [addBankName, setAddBankName] = useState("");
  const [addOpenBranchName, setAddOpenBranchName] = useState("");
  const [addAccountErrorMessage, setAddAccountErrorMessage] = useState("");
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailItem, setDetailItem] = useState<CardPaymentRecordItem>();
  const [eReceiptVisible, setEReceiptVisible] = useState(false);
  const [eReceiptPaymentId, setEReceiptPaymentId] = useState("");
  const [eReceiptItems, setEReceiptItems] = useState<CardPaymentEReceiptItem[]>([]);
  const [eReceiptErrorMessage, setEReceiptErrorMessage] = useState("");
  const [eReceiptBusyAction, setEReceiptBusyAction] = useState<
    "apply" | "query" | "download" | null
  >(null);
  const [downloadingRcptNum, setDownloadingRcptNum] = useState("");

  const overviewMutation = useMutation({
    mutationFn: (input: { page: number; payAccountNo?: string }) =>
      fetchCardPaymentOverview(input.page, PAYMENT_RECORD_PAGE_SIZE, input.payAccountNo),
  });
  const transferMutation = useMutation({
    mutationFn: (input: {
      amount: number;
      payAccountNo: string;
      receiveAccountNo: string;
      receiveAccountName: string;
      receiveOpenBankName: string;
      payAccountType: "00" | "01";
    }) => createCardPaymentTransfer(input),
  });
  const addAccountMutation = useMutation({
    mutationFn: createCardPaymentAccount,
  });
  const cardRefreshQuery = useQuery({
    queryKey: ["payment-validation", "card-payment-overview-refresh", page, selectedAccountNo],
    queryFn: () =>
      fetchCardPaymentOverview(page, PAYMENT_RECORD_PAGE_SIZE, selectedAccountNo || undefined),
    enabled: Boolean(result),
    refetchInterval: PAYMENT_RECORD_POLL_INTERVAL_MS,
    retry: false,
  });

  useEffect(() => {
    if (cardRefreshQuery.data) {
      setResult(cardRefreshQuery.data);
      if (cardRefreshQuery.data.account.account_no) {
        setSelectedAccountNo(cardRefreshQuery.data.account.account_no);
      }
    }
  }, [cardRefreshQuery.data]);

  /** 拉取指定页卡支付总览。 */
  async function loadOverview(selectedPage: number, payAccountNo?: string) {
    setErrorMessage("");
    try {
      const nextResult = await overviewMutation.mutateAsync({
        page: selectedPage,
        payAccountNo: payAccountNo || undefined,
      });
      setResult(nextResult);
      setPage(selectedPage);
      if (nextResult.account.account_no) {
        setSelectedAccountNo(nextResult.account.account_no);
      }
    } catch (error) {
      setResult(undefined);
      setErrorMessage(paymentValidationErrorMessage(error, "卡支付查询失败，请稍后重试。"));
    }
  }

  useEffect(() => {
    void loadOverview(1);
  }, []);

  /** 清空转账弹窗表单字段。 */
  function resetTransferForm() {
    setTransferAmount(undefined);
    setTransferReceiveAccountNo("");
    setTransferReceiveAccountName("");
    setTransferReceiveOpenBankName("");
    setTransferPayAccountType("01");
    setTransferErrorMessage("");
  }

  /** 清空新增付款账户表单。 */
  function resetAddAccountForm() {
    setAddAccountNo("");
    setAddAccountName("");
    setAddBankName("");
    setAddOpenBranchName("");
    setAddAccountErrorMessage("");
  }

  return {
    page,
    pageSize: PAYMENT_RECORD_PAGE_SIZE,
    selectedAccountNo,
    result,
    errorMessage,
    loading: overviewMutation.isPending || addAccountMutation.isPending,
    transferring: transferMutation.isPending,
    transferVisible,
    transferAmount,
    transferReceiveAccountNo,
    transferReceiveAccountName,
    transferReceiveOpenBankName,
    transferPayAccountType,
    transferErrorMessage,
    addAccountVisible,
    addAccountNo,
    addAccountName,
    addBankName,
    addOpenBranchName,
    addAccountErrorMessage,
    addingAccount: addAccountMutation.isPending,
    detailVisible,
    detailItem,
    eReceiptVisible,
    eReceiptPaymentId,
    eReceiptItems,
    eReceiptErrorMessage,
    eReceiptBusyAction,
    downloadingRcptNum,
    setTransferAmount(value: number | undefined) {
      setTransferAmount(value);
    },
    setTransferReceiveAccountNo(value: string) {
      setTransferReceiveAccountNo(value);
    },
    setTransferReceiveAccountName(value: string) {
      setTransferReceiveAccountName(value);
    },
    setTransferReceiveOpenBankName(value: string) {
      setTransferReceiveOpenBankName(value);
    },
    setTransferPayAccountType(value: "00" | "01") {
      setTransferPayAccountType(value);
    },
    setAddAccountNo(value: string) {
      setAddAccountNo(value);
    },
    setAddAccountName(value: string) {
      setAddAccountName(value);
    },
    setAddBankName(value: string) {
      setAddBankName(value);
    },
    setAddOpenBranchName(value: string) {
      setAddOpenBranchName(value);
    },
    async handleRefresh() {
      await loadOverview(page, selectedAccountNo || undefined);
    },
    async handlePageChange(nextPage: number) {
      await loadOverview(nextPage, selectedAccountNo || undefined);
    },
    async handleSelectAccount(accountNo: string) {
      const normalized = accountNo.replace(/\s+/g, "");
      if (!normalized || normalized === selectedAccountNo) {
        return;
      }
      setSelectedAccountNo(normalized);
      await loadOverview(1, normalized);
    },
    openTransfer() {
      resetTransferForm();
      setTransferVisible(true);
    },
    closeTransfer() {
      setTransferVisible(false);
      resetTransferForm();
    },
    openAddAccount() {
      resetAddAccountForm();
      setAddAccountVisible(true);
    },
    closeAddAccount() {
      setAddAccountVisible(false);
      resetAddAccountForm();
    },
    async submitAddAccount() {
      const accountNo = addAccountNo.replace(/\s+/g, "");
      const accountName = addAccountName.trim();
      const bankName = addBankName.trim();
      const openBranchName = addOpenBranchName.trim();
      if (!accountNo || !accountName || !bankName || !openBranchName) {
        setAddAccountErrorMessage("请完整填写账号、名称、银行和开户网点");
        return;
      }
      setAddAccountErrorMessage("");
      try {
        await addAccountMutation.mutateAsync({
          accountNo,
          accountName,
          bankName,
          openBranchName,
        });
        setAddAccountVisible(false);
        resetAddAccountForm();
        await loadOverview(1, accountNo);
      } catch (error) {
        setAddAccountErrorMessage(
          paymentValidationErrorMessage(error, "新增付款账户失败，请稍后重试。"),
        );
      }
    },
    async submitTransfer() {
      const receiveOpenBankName = transferReceiveOpenBankName.trim();
      const receiveAccountNo = transferReceiveAccountNo.replace(/\s+/g, "");
      const receiveAccountName = transferReceiveAccountName.trim();
      const payAccountNo = selectedAccountNo || result?.account.account_no || "";
      if (!payAccountNo) {
        setTransferErrorMessage("请先选择付款账号");
        return;
      }
      if (!receiveOpenBankName) {
        setTransferErrorMessage("请选择开户行");
        return;
      }
      if (!receiveAccountNo) {
        setTransferErrorMessage("请输入收款卡号");
        return;
      }
      if (!receiveAccountName) {
        setTransferErrorMessage("请输入收款户名");
        return;
      }
      if (transferAmount == null || !(transferAmount > 0)) {
        setTransferErrorMessage("请输入大于 0 的付款金额");
        return;
      }
      setTransferErrorMessage("");
      try {
        await transferMutation.mutateAsync({
          amount: Number(transferAmount.toFixed(2)),
          payAccountNo,
          receiveAccountNo,
          receiveAccountName,
          receiveOpenBankName,
          payAccountType: transferPayAccountType,
        });
        setTransferVisible(false);
        resetTransferForm();
        await loadOverview(1, payAccountNo);
      } catch (error) {
        setTransferErrorMessage(
          paymentValidationErrorMessage(error, "转账提交失败，请稍后重试。"),
        );
      }
    },
    openDetail(item: CardPaymentRecordItem) {
      setDetailItem(item);
      setDetailVisible(true);
    },
    closeDetail() {
      setDetailVisible(false);
      setDetailItem(undefined);
    },
    async applyEReceipt(item: CardPaymentRecordItem) {
      if (item.record_type === "refund") {
        return;
      }
      setEReceiptBusyAction("apply");
      setEReceiptErrorMessage("");
      try {
        await applyCardPaymentEReceipt(item.id);
        Message.success("电子回单申请已提交");
      } catch (error) {
        Message.error(paymentValidationErrorMessage(error, "电子回单申请失败，请稍后重试。"));
      } finally {
        setEReceiptBusyAction(null);
      }
    },
    async queryEReceipt(item: CardPaymentRecordItem) {
      if (item.record_type === "refund") {
        return;
      }
      setEReceiptBusyAction("query");
      setEReceiptErrorMessage("");
      setEReceiptPaymentId(item.id);
      try {
        const items = await queryCardPaymentEReceipt(item.id);
        setEReceiptItems(items);
        setEReceiptVisible(true);
        if (items.length === 0) {
          setEReceiptErrorMessage("暂无电子回单，可先申请后再查询");
        }
      } catch (error) {
        setEReceiptItems([]);
        setEReceiptVisible(true);
        setEReceiptErrorMessage(
          paymentValidationErrorMessage(error, "电子回单查询失败，请稍后重试。"),
        );
      } finally {
        setEReceiptBusyAction(null);
      }
    },
    closeEReceipt() {
      setEReceiptVisible(false);
      setEReceiptPaymentId("");
      setEReceiptItems([]);
      setEReceiptErrorMessage("");
      setDownloadingRcptNum("");
    },
    async downloadEReceipt(rcptNum: string) {
      if (!eReceiptPaymentId || !rcptNum) {
        return;
      }
      setEReceiptBusyAction("download");
      setDownloadingRcptNum(rcptNum);
      setEReceiptErrorMessage("");
      try {
        const file = await downloadCardPaymentEReceipt(eReceiptPaymentId, rcptNum);
        downloadBase64File(file.file_content, file.file_name || `${rcptNum}.zip`);
        Message.success("电子回单已开始下载");
      } catch (error) {
        setEReceiptErrorMessage(
          paymentValidationErrorMessage(error, "电子回单下载失败，请稍后重试。"),
        );
      } finally {
        setEReceiptBusyAction(null);
        setDownloadingRcptNum("");
      }
    },
  };
}

/** 将 base64 文件内容触发浏览器下载。 */
function downloadBase64File(base64Content: string, fileName: string) {
  const binary = window.atob(base64Content);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const blob = new Blob([bytes], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
